/**
 * Glass toolkit (user round) — the marble's outer shell as REAL glass,
 * and glass/wire render variants for tunnel arches.
 *
 * Research-backed (Codrops glass/vortex-sphere; three.js transmission
 * pipeline): kit marbles keep their authored two-part construction —
 * opaque colored core inside a shell — and the shell's material becomes
 * MeshPhysicalMaterial transmission glass, TINTED VIA BEER–LAMBERT
 * ABSORPTION (attenuationColor), never by multiplying the base color
 * (that read as paint, not glass — "too aggressive"). The opaque core
 * is what the shell refracts. Big instanced packs get an additive
 * fresnel shell instead (order-independent — 10k unsorted transparent
 * instances cannot artifact on a dark sky).
 *
 * Environment: glass is reflection + refraction; the void has neither,
 * so ONE PMREM RoomEnvironment texture feeds material.envMap on glass
 * materials only — scene.environment stays unset (it would relight the
 * whole dark world). That environment is BRIGHTER THAN THE WORLD, so
 * marble shells keep exactly one reflection layer at half intensity —
 * never a clearcoat stacked over the glass's own Fresnel (see
 * glassShellMaterial for the measurement).
 *
 * Tunnels: glass tunnels use NON-transmission transparent glass — a
 * transmissive arch would erase transmissive marbles behind it (only
 * opaque objects land in the transmission buffer); alpha-blend keeps
 * the pack visible through the arch. Wire tunnels are the wireframe
 * lattice of the same arch mesh.
 */
import * as THREE from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import type { RacerLook } from "./peersCore.ts";

let env: THREE.Texture | null = null;

/** One shared PMREM environment for every glass material (lazy). */
export function marbleEnv(renderer: THREE.WebGLRenderer): THREE.Texture {
  if (!env) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    env = pmrem.fromScene(new RoomEnvironment()).texture;
    pmrem.dispose();
  }
  return env;
}

/** Kit-marble shell glass: transmission with the phenotype SHELL as the
 * absorption tint (white shell = clear glass); ghost marbles run a long
 * attenuation distance (clearer), pearls carry iridescence. */
export function glassShellMaterial(
  renderer: THREE.WebGLRenderer,
  look: RacerLook,
): THREE.MeshPhysicalMaterial {
  const ghost = look.effect.kind === "ghost";
  return new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    transmission: 1,
    transparent: false,
    roughness: 0.04,
    metalness: 0,
    // Thin SHELL, not solid glass: thickness is a world-space volume
    // depth — at 0.2 (the marble's full diameter) refraction displaced
    // the core's virtual image visibly off-center (user report: "centers
    // offset while physics is true-centered"). ~0.03 reads as a skin of
    // glass over a solid core, image stays concentric.
    ior: 1.45,
    thickness: 0.03,
    attenuationColor: new THREE.Color(
      look.shell[0],
      look.shell[1],
      look.shell[2],
    ),
    attenuationDistance: ghost ? 0.6 : 0.15,
    // ONE reflection layer, at half strength (user: "the outer shell gets a
    // blue tint at some locations or angles"). Transmission ADDS specular on
    // top of the refracted core rather than blending with it, so a hot
    // environment washes the core out. RoomEnvironment's PMREM is an indoor
    // room with emissive panels well above 1.0, and `clearcoat: 1` stacked a
    // SECOND full Fresnel layer over the glass's own — together ~8% of a very
    // bright env sprayed neutral light over the whole sphere, which the zone's
    // mood lighting then tinted (periwinkle under the lavender moods). Measured
    // on a red-cored marble: the wash lifted G/B from ~4 to ~47. Dropping the
    // redundant clearcoat and halving the intensity cuts it to ~a fifth, and
    // core saturation at race distance rises ~40%.
    envMap: marbleEnv(renderer),
    envMapIntensity: 0.5,
    iridescence: look.effect.kind === "pearl"
      ? 0.35 + 0.5 * look.effect.intensity
      : 0,
  });
}

/** Kit marbles are NOT authored core+shell (measured 2026-08-09): the
 * plain variants are an EMPTY glass sphere and the center variants hide
 * only a flat decal disc at the shell's mid-plane — nothing solid to
 * refract, so at race distance every marble read as a washed-out bubble
 * (user report). This injects the missing core: an opaque sphere at
 * 0.78 of the shell, added to the kit mesh BEFORE the material pass so
 * identity tint / effects / boost glow all apply through the same
 * pipeline. The core anchors to the MEASURED shell bounds — the
 * marble-center-* piece roots carry a +0.2 node translation the plain
 * variants lack (second user report: hardcoding any center left those
 * cores hanging at the bottom of the shell). */
export function addMarbleCore(mesh: THREE.Object3D): void {
  mesh.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(mesh);
  if (box.isEmpty()) return;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  mesh.worldToLocal(center);
  // Shell radius in the mesh's LOCAL frame (setFromObject measured in
  // parent space — undo the mesh's own scale).
  const radius = Math.max(size.x, size.y, size.z) / 2 /
    (Math.abs(mesh.scale.x) || 1);
  const geo = new THREE.SphereGeometry(radius * 0.78, 20, 14);
  const mat = new THREE.MeshStandardMaterial({
    color: 0xdfe6ee,
    roughness: 0.35,
    metalness: 0.05,
  });
  mat.name = "core";
  const m = new THREE.Mesh(geo, mat);
  m.name = "core";
  // Unlike the cloned kit shell, this geometry/material pair is created
  // per marble and must be released on same-page regeneration.
  m.userData.marbleOwnedVisual = true;
  m.position.copy(center);
  mesh.add(m);
}

/** Dispose only the per-marble geometry/material injected above. Kit shell
 * clones share their loaded assets and must remain alive for later seeds. */
export function disposeMarbleOwnedVisuals(root: THREE.Object3D): void {
  root.traverse((child: THREE.Object3D) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.userData.marbleOwnedVisual) return;
    mesh.geometry.dispose();
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    for (const material of materials) material.dispose();
  });
}

/** Additive fresnel shell for instanced packs: rim-only glass illusion,
 * order-independent (no sorting artifacts at any N). Declares the
 * instancing attributes itself (raw ShaderMaterial gets none for free);
 * per-instance tint via instanceColor. */
export function fresnelShellMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    vertexShader: `
      // instanceMatrix/instanceColor are injected by three's prelude for
      // materials on an InstancedMesh — declaring them again collides.
      varying vec3 vN;
      varying vec3 vW;
      varying vec3 vTint;
      void main() {
        vTint = instanceColor;
        vec4 wp = modelMatrix * instanceMatrix * vec4(position, 1.0);
        vW = wp.xyz;
        vN = normalize(mat3(modelMatrix) * mat3(instanceMatrix) * normal);
        gl_Position = projectionMatrix * viewMatrix * wp;
      }
    `,
    fragmentShader: `
      varying vec3 vN;
      varying vec3 vW;
      varying vec3 vTint;
      void main() {
        vec3 V = normalize(cameraPosition - vW);
        float f = pow(1.0 - abs(dot(normalize(vN), V)), 3.5);
        vec3 c = vTint * f * 0.85 + vec3(0.22) * f * f;
        gl_FragColor = vec4(c, 1.0);
      }
    `,
  });
}

let chainlink: THREE.DataTexture | null = null;

/** Procedural chainlink lattice (alpha cutout): a diamond weave, not the
 * model's triangulation (user report — `wireframe` rendered the literal
 * mesh edges). Shared by every wire arch. */
function chainlinkTexture(): THREE.DataTexture {
  if (chainlink) return chainlink;
  const S = 64;
  const P = 16; // weave period
  const W = 2.2; // strand half-width
  const data = new Uint8Array(S * S * 4);
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const a = Math.abs(((x + y) % P) - P / 2) < W;
      const b = Math.abs(((x - y + S) % P) - P / 2) < W;
      const on = a || b ? 255 : 0;
      const i = (y * S + x) * 4;
      data[i] = on;
      data[i + 1] = on;
      data[i + 2] = on;
      data[i + 3] = 255;
    }
  }
  chainlink = new THREE.DataTexture(data, S, S);
  chainlink.wrapS = THREE.RepeatWrapping;
  chainlink.wrapT = THREE.RepeatWrapping;
  chainlink.magFilter = THREE.LinearFilter;
  chainlink.minFilter = THREE.LinearMipmapLinearFilter;
  chainlink.generateMipmaps = true;
  chainlink.needsUpdate = true;
  return chainlink;
}

/** Deterministic per-placement hash (position is seed material — the
 * same recipe as island color jitter). */
function posHash(x: number, y: number, z: number): number {
  let h = Math.imul(Math.round(x * 8) | 0, 374761393) ^
    Math.imul(Math.round(y * 8) | 0, 668265263) ^
    Math.imul(Math.round(z * 8) | 0, 2147483647);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Tunnel render variants (user round): some arches draw as GLASS
 * (alpha-blend translucent — the pack stays visible racing through),
 * some as WIRE lattice. Position-hashed → deterministic per seed, no
 * generation impact. Returns disposables for the segment. */
export function applyTunnelVariants(
  group: THREE.Object3D,
  renderer: THREE.WebGLRenderer,
): {
  meshes: THREE.InstancedMesh[];
  cameraFadeMeshes: THREE.Mesh[];
  dispose(): void;
} {
  const cloned: THREE.Material[] = [];
  const clonedGeos: THREE.BufferGeometry[] = [];
  const cameraFadeMeshes: THREE.Mesh[] = [];
  group.traverse((o: THREE.Object3D) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || m.name !== "tunnel") return;
    const p = m.getWorldPosition(new THREE.Vector3());
    const h = posHash(p.x, p.y, p.z);
    if (h < 0.2) {
      // Glass arch: tinted alpha glass (non-transmission — see header).
      const mat = new THREE.MeshPhysicalMaterial({
        color: 0xbfe2f5,
        transparent: true,
        opacity: 0.32,
        roughness: 0.08,
        metalness: 0,
        clearcoat: 1,
        clearcoatRoughness: 0.1,
        envMap: marbleEnv(renderer),
        envMapIntensity: 1.0,
        side: THREE.DoubleSide,
        depthWrite: false,
        // The arch flange lies on the track rim — push the glass back so
        // the opaque rim wins the contact line cleanly.
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      });
      cloned.push(mat);
      m.material = mat;
      m.geometry.computeBoundingBox();
      m.userData.cameraBaseOpacity = mat.opacity;
      cameraFadeMeshes.push(m);
    } else if (h < 0.34) {
      // Wire arch: a chainlink CAGE — alpha-cutout diamond weave over
      // projected UVs (the kit's atlas UVs can't tile a repeat; the
      // arch curves in x/y and runs along z, so (x+y, z) projects a
      // continuous wrap). Geometry is cloned — the kit shares it.
      const geo = m.geometry.clone();
      const pos = geo.getAttribute("position");
      const uv = new Float32Array(pos.count * 2);
      for (let i = 0; i < pos.count; i++) {
        uv[i * 2] = (pos.getX(i) + pos.getY(i)) * 3.0;
        uv[i * 2 + 1] = pos.getZ(i) * 3.0;
      }
      geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
      m.geometry = geo;
      clonedGeos.push(geo);
      const base = (Array.isArray(m.material)
        ? m.material[0]
        : m.material) as THREE.MeshStandardMaterial;
      const mat = base.clone();
      mat.map = null;
      mat.alphaMap = chainlinkTexture();
      mat.alphaTest = 0.5;
      mat.side = THREE.DoubleSide;
      mat.color.multiplyScalar(0.85);
      // Lattice on the rim surface: pull the weave forward.
      mat.polygonOffset = true;
      mat.polygonOffsetFactor = -1;
      mat.polygonOffsetUnits = -1;
      mat.transparent = true;
      mat.depthWrite = false;
      cloned.push(mat);
      m.material = mat;
      m.geometry.computeBoundingBox();
      m.userData.cameraBaseOpacity = 1;
      cameraFadeMeshes.push(m);
    }
  });
  return {
    meshes: [],
    cameraFadeMeshes,
    dispose() {
      for (const m of cloned) m.dispose();
      for (const g of clonedGeos) g.dispose();
    },
  };
}
